import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { SettingsType } from '../../../../shared/api/pipeex/pcfSettingsClient';
import {
  borderRadius, getSemanticSurfaceTokens, layoutGap,
} from '../../../../theme/theme';
import { OperationProgressIndicator } from '../../../../shared/ui/progress';
import {
  CaptionText,
  CompactPageTitle,
  OverlineText,
} from '../../../../shared/ui/typography';

const DefaultProfileName = 'PipeEXUI Design';

interface ProfileSaveAsDialogProps {
  open: boolean;
  settingsType: SettingsType;
  isSaving: boolean;
  mode?: 'saveAs' | 'rename';
  initialProfileName?: string;
  onClose: () => void;
  onSave: (profileName: string) => Promise<boolean> | boolean;
}

const scopeLabel = (settingsType: SettingsType): string =>
  settingsType === SettingsType.Company ? 'Company profile' : 'User profile';

const ProfileSaveAsDialog: React.FC<ProfileSaveAsDialogProps> = ({
  open,
  settingsType,
  isSaving,
  mode = 'saveAs',
  initialProfileName,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const [profileName, setProfileName] = useState(DefaultProfileName);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setProfileName(initialProfileName ?? DefaultProfileName);
      setHasSubmitted(false);
    }
  }, [initialProfileName, open]);

  const isRename = mode === 'rename';

  const trimmedProfileName = profileName.trim();
  const profileNameMissing = hasSubmitted && !trimmedProfileName;

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasSubmitted(true);

    if (!trimmedProfileName || isSaving) {
      return;
    }

    const saved = await onSave(trimmedProfileName);
    if (saved) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="profile-name-dialog-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${colors.border.strong}`,
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: `0 18px 42px ${alpha(theme.palette.common.black, 0.22)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="profile-name-dialog-title"
        sx={{
          px: 2,
          pt: 1.65,
          pb: 0.9,
          borderBottom: `1px solid ${colors.border.light}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={layoutGap.row}>
          <Box sx={{ minWidth: 0 }}>
            <OverlineText
              sx={{
                display: 'block',
                color: colors.text.tertiary,
              }}
            >
              Mapping profile
            </OverlineText>
            <CompactPageTitle
              component="span"
              sx={{
                display: 'block',
                mt: 0.25,
                color: colors.text.primary,
              }}
            >
              {isRename ? 'Rename mapping profile' : 'Save a new mapping profile'}
            </CompactPageTitle>
          </Box>
          <Box
            sx={{
              px: 0.5,
              py: 0.35,
              flexShrink: 0,
              borderRadius: `${borderRadius.sm}px`,
              color: colors.text.secondary,
              border: `1px solid ${colors.border.default}`,
              bgcolor: colors.background.card,
            }}
          >
            <CaptionText>{scopeLabel(settingsType)}</CaptionText>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pt: 2.4, pb: 0.75 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Profile name"
          value={profileName}
          disabled={isSaving}
          error={profileNameMissing}
          helperText={profileNameMissing ? 'Enter a name so PipeEX can identify the saved mapping settings.' : ' '}
          inputProps={{ maxLength: 120 }}
          onChange={(event) => setProfileName(event.target.value)}
          sx={{
            mt: 0.5,
            '& .MuiInputBase-root': {
              borderRadius: `${borderRadius.sm}px`,
              fontSize: '0.8125rem',
            },
            '& .MuiFormHelperText-root': {
              minHeight: 18,
              mx: 0,
              fontSize: '0.6875rem',
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          pt: 0.5,
          pb: 1.5,
          gap: 0.5,
        }}
      >
        <Button
          type="button"
          size="small"
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            minHeight: 30,
            borderRadius: `${borderRadius.sm}px`,
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={isSaving}
          startIcon={
            isSaving ? (
              <OperationProgressIndicator
                size={14}
                color="inherit"
                ariaLabel={isRename ? 'Renaming the selected mapping profile' : 'Saving current settings as a new mapping profile'}
              />
            ) : (
              isRename
                ? <EditIcon sx={{ fontSize: '0.875rem' }} />
                : <SaveIcon sx={{ fontSize: '0.875rem' }} />
            )
          }
          sx={{
            minHeight: 30,
            borderRadius: `${borderRadius.sm}px`,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          }}
        >
          {isRename ? 'Rename profile' : 'Save new profile'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ProfileSaveAsDialog);
